// VerticalFeed — mobile-first compulsory full-bleed swipe feed (default for /watch on mobile)
import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Heart, MessageCircle, Share2, UserPlus, Sparkles, X, ChevronUp, ChevronDown, Smile } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import LiveKitStreamRoom from "../components/LiveKitStreamRoom";
import FloatingStickers from "../components/FloatingStickers";
import SponsorBanner from "../components/SponsorBanner";
import StoriesRail from "../components/StoriesRail";

export default function VerticalFeed() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const startStream = params.get("stream");
  const [streams, setStreams] = useState([]);
  const [idx, setIdx] = useState(0);
  const [trigger, setTrigger] = useState(null);
  const [presence, setPresence] = useState(null); // live participant count from WS
  const [showChat, setShowChat] = useState(false);
  const [showStickers, setShowStickers] = useState(false);
  const [stickers, setStickers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [overlayMsgs, setOverlayMsgs] = useState([]);   // floating TikTok-style chat overlay
  const [chatInput, setChatInput] = useState("");
  const wsRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    api.get("/streams", { params: { live_only: true } })
      .then(({ data }) => {
        const live = data.filter((s) => s.is_live);
        setStreams(live);
        if (startStream) {
          const i = live.findIndex((s) => s.id === startStream);
          if (i >= 0) setIdx(i);
        }
      })
      .finally(() => setLoaded(true));
    api.get("/stickers").then(({ data }) => setStickers(data)).catch(() => {});
  }, [startStream]);

  // Subscribe to the current stream's chat WebSocket → floating overlay messages.
  // Uses a 20s keepalive ping + auto-reconnect on drop so intermediate proxies
  // (which routinely close idle WebSockets after ~30s) don't leave the client
  // with a dead socket that silently misses messages.
  const currentStream = streams[idx];
  useEffect(() => {
    if (!currentStream) return;
    // Pull recent history once
    api.get(`/streams/${currentStream.id}`)
      .then(({ data }) => setOverlayMsgs((data.chat || []).slice(-15)))
      .catch(() => setOverlayMsgs([]));

    let ws;
    let pingTimer = null;
    let reconnectTimer = null;
    let closedByUnmount = false;

    const handleMessage = (evt) => {
      try {
        const m = JSON.parse(evt.data);
        if (m?.type === "chat" && m?.payload) {
          setOverlayMsgs((arr) => (m.payload.id && arr.some((x) => x.id === m.payload.id)) ? arr : [...arr.slice(-30), m.payload]);
        }
        if (m?.type === "presence" && typeof m.count === "number") {
          setPresence(m.count);
        }
        if (m?.type === "sticker" && m?.payload) {
          setTrigger({
            emoji: m.payload.emoji,
            color: m.payload.color,
            animation: m.payload.animation,
            sound_url: m.payload.sound_url,
            price: m.payload.price,
            burst: m.payload.burst || 3,
            key: Date.now(),
          });
        }
      } catch { /* noop */ }
    };

    const open = () => {
      if (closedByUnmount) return;
      try {
        const wsUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws") + `/api/ws/chat/${currentStream.id}`;
        ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onopen = () => {
          // Send a small ping every 20s. The backend replies with {type:"pong"}
          // — the pong itself doesn't matter, the periodic *traffic* is what
          // keeps intermediate proxies from closing the idle socket.
          if (pingTimer) clearInterval(pingTimer);
          pingTimer = setInterval(() => {
            try { if (ws && ws.readyState === 1) ws.send(JSON.stringify({ type: "ping" })); } catch { /* noop */ }
          }, 20000);
        };
        ws.onmessage = handleMessage;
        ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
        ws.onclose = () => {
          if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
          if (closedByUnmount) return;
          // Auto-reconnect after a short delay
          if (reconnectTimer) clearTimeout(reconnectTimer);
          reconnectTimer = setTimeout(open, 1500);
        };
      } catch { /* noop */ }
    };

    open();

    return () => {
      closedByUnmount = true;
      if (pingTimer) clearInterval(pingTimer);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { ws?.close(); } catch { /* noop */ }
    };
  }, [currentStream?.id]);

  const sendOverlayChat = async () => {
    const txt = chatInput.trim();
    if (!txt || !currentStream || !user) {
      if (!user) nav("/auth");
      return;
    }
    setChatInput("");
    try {
      const { data: msg } = await api.post(`/streams/${currentStream.id}/chat`, { stream_id: currentStream.id, message: txt });
      // Local optimistic append — guarded by id so we don't double-render if the
      // server's WebSocket broadcast has already added this same message.
      setOverlayMsgs((arr) => (msg?.id && arr.some((x) => x.id === msg.id)) ? arr : [...arr.slice(-30), msg]);
    } catch (e) {
      if (e?.response?.status === 400) toast.error(e?.response?.data?.detail || "Blocked");
      else toast.error("Chat failed to send. Try again.");
    }
  };

  useEffect(() => {
    const onWheel = (e) => {
      if (Math.abs(e.deltaY) < 30) return;
      if (e.deltaY > 0) setIdx((i) => Math.min(streams.length - 1, i + 1));
      else setIdx((i) => Math.max(0, i - 1));
    };
    const onKey = (e) => {
      if (e.key === "ArrowDown") setIdx((i) => Math.min(streams.length - 1, i + 1));
      if (e.key === "ArrowUp") setIdx((i) => Math.max(0, i - 1));
    };
    let touchY = 0;
    const onTouchStart = (e) => { touchY = e.touches[0].clientY; };
    const onTouchEnd = (e) => {
      const dy = touchY - e.changedTouches[0].clientY;
      if (Math.abs(dy) < 40) return;
      if (dy > 0) setIdx((i) => Math.min(streams.length - 1, i + 1));
      else setIdx((i) => Math.max(0, i - 1));
    };
    window.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("keydown", onKey);
    const c = containerRef.current;
    c?.addEventListener("touchstart", onTouchStart, { passive: true });
    c?.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("keydown", onKey);
      c?.removeEventListener("touchstart", onTouchStart);
      c?.removeEventListener("touchend", onTouchEnd);
    };
  }, [streams.length]);

  const s = streams[idx];

  if (!loaded) return <div className="h-screen flex items-center justify-center text-white/40" data-testid="feed-loading">Loading the feed…</div>;
  if (!streams.length) return (
    <>
      <StoriesRail />
      <div className="h-[calc(100vh-80px)] flex flex-col items-center justify-center gap-3 text-center px-6" data-testid="feed-empty">
        <div className="text-white/80 text-lg font-display tracking-wide">// no live streams right now</div>
        <div className="text-white/40 text-sm max-w-md">When creators go live they appear here as a TikTok-style vertical feed. Try the <a href="/explore" className="text-nexus-cyan underline">Explore</a> page or head to <a href="/studio" className="text-nexus-cyan underline">Studio</a> to go live yourself.</div>
      </div>
    </>
  );
  if (!s) return null;

  const sendSticker = async (sticker) => {
    if (!user) return nav("/auth");
    // Block + prompt if user can't afford a paid sticker
    if (sticker.price > 0 && (user.wallet_balance || 0) < sticker.price) {
      toast.error(`You need ${sticker.price} NX for ${sticker.name}. Redirecting to top-up…`);
      setTimeout(() => nav(`/coins?need=${sticker.price}&for=sticker`), 1100);
      return;
    }
    setTrigger({ emoji: sticker.emoji, color: sticker.color, animation: sticker.animation,
                 sound_url: sticker.sound_url, price: sticker.price, burst: sticker.burst || 4, key: Date.now() });
    // Broadcast sticker + a chat bubble to all other viewers via WS.
    const senderName = user?.display_name || "Guest";
    const chatBubble = {
      id: `local-${Date.now()}`,
      user_name: senderName,
      message: `sent ${sticker.emoji} ${sticker.name}${sticker.price > 0 ? ` · ${sticker.price} NX` : ""}`,
      is_sticker_event: true,
      sticker_color: sticker.color,
      ts: new Date().toISOString(),
    };
    setOverlayMsgs((arr) => [...arr.slice(-30), chatBubble]);
    try {
      const wsUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws") + `/api/ws/chat/${s.id}`;
      const ws = new WebSocket(wsUrl);
      ws.onopen = () => {
        ws.send(JSON.stringify({ type: "sticker", payload: { emoji: sticker.emoji, color: sticker.color, animation: sticker.animation, sound_url: sticker.sound_url, price: sticker.price, burst: sticker.burst || 3 } }));
        ws.send(JSON.stringify({ type: "chat", payload: chatBubble }));
        setTimeout(() => ws.close(), 200);
      };
    } catch { /* WS send is best-effort */ }
    if (sticker.price > 0) {
      try { await api.post("/tips", { stream_id: s.id, amount: sticker.price, sticker_id: sticker.id, message: `${sticker.name} sticker` }); }
      catch (err) {
        const detail = err?.response?.data?.detail;
        if (detail && detail.code === "INSUFFICIENT_NX") {
          toast.error(detail.message || "Insufficient NX. Buy a bundle.");
          setTimeout(() => nav(`/coins?need=${detail.needed}&for=sticker`), 1000);
        }
      }
    }
    // SMASHABLE — keep the dropdown open so the user can rapid-fire stickers
  };

  const tip = async () => {
    if (!user) return nav("/auth");
    try {
      await api.post("/tips", { stream_id: s.id, amount: 10, message: "🔥" });
      setTrigger({ emoji: "🔥", color: "#FF2E93", animation: "flame-up", price: 20, burst: 5, key: Date.now() });
    } catch { /* noop */ }
  };

  return (
    <div ref={containerRef} className="fixed inset-0 bg-black z-50 flex flex-col" data-testid="vertical-feed">
      <StoriesRail />
      {/* close */}
      <button onClick={() => nav(-1)} className="absolute top-4 right-4 z-50 bg-black/60 text-white p-2 rounded-full" data-testid="close-feed"><X className="w-4 h-4" /></button>

      {/* stream stage -- "nx-stream-stage" forces object-fit: contain (see index.css) so a
          landscape stream (OBS/webcam, the common case) is never cropped inside this
          full-bleed portrait tile; it letterboxes instead of cutting off the sides. */}
      <div className="absolute inset-0 nx-stream-stage">
        {s.livekit_room
          ? <LiveKitStreamRoom streamId={s.id} role={user?.id === s.creator_id ? "self_view" : "viewer"} fallback={<video src={s.video_url || "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4"} className="w-full h-full object-contain" autoPlay loop muted playsInline />} />
          : <video src={s.video_url} className="w-full h-full object-contain" autoPlay loop muted playsInline />}
        <FloatingStickers trigger={trigger} />
      </div>

      {/* gradient overlays */}
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/80 to-transparent pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-black/95 via-black/40 to-transparent pointer-events-none" />

      {/* LIVE badge */}
      <div className="absolute top-4 left-4 flex items-center gap-2 z-30">
        <div className="bg-nexus-pink/95 px-3 py-1 text-[10px] tracking-[0.3em] font-display font-bold uppercase animate-pulse rounded">● LIVE</div>
        {presence !== null && (
          <div
            className="bg-black/60 backdrop-blur-md border border-nexus-cyan/50 text-nexus-cyan px-2.5 py-1 text-[10px] tracking-[0.25em] uppercase font-display flex items-center gap-1.5 rounded-full"
            data-testid="presence-pill"
            title="Live participants (chat + video)"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-nexus-cyan animate-pulse" />
            {presence}
          </div>
        )}
        {user && user.id === s.creator_id && s.is_live && (
          <button
            onClick={async () => {
              if (!window.confirm("End this live stream? Viewers will be dropped.")) return;
              try {
                await api.post(`/studio/end/${s.id}`);
                toast.success("Stream ended.");
                nav("/studio");
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Couldn't end stream");
              }
            }}
            data-testid="end-stream-btn"
            className="bg-nexus-crimson hover:bg-nexus-crimson/80 text-white px-3 py-1 rounded text-[10px] tracking-[0.3em] font-display font-bold uppercase transition-colors border border-nexus-crimson/60 shadow"
            title="Only you can see this button"
          >
            ■ End
          </button>
        )}
      </div>

      {/* nav arrows (desktop) */}
      <button onClick={() => setIdx((i) => Math.max(0, i - 1))} className="hidden lg:flex absolute top-1/2 left-4 -translate-y-1/2 z-30 bg-black/60 hover:bg-nexus-purple/60 backdrop-blur-md p-3 rounded-full transition-colors"><ChevronUp className="w-5 h-5" /></button>
      <button onClick={() => setIdx((i) => Math.min(streams.length - 1, i + 1))} className="hidden lg:flex absolute bottom-1/2 left-4 translate-y-1/2 mt-12 z-30 bg-black/60 hover:bg-nexus-purple/60 backdrop-blur-md p-3 rounded-full transition-colors"><ChevronDown className="w-5 h-5" /></button>

      {/* TikTok-style LIVE CHAT OVERLAY — semi-transparent floating messages on left.
          Positioned so the strip lives BETWEEN the top LIVE badge/presence pill and
          the bottom info block (title, viewer count, sponsor banner), never covering
          either. flex-col-reverse + .reverse() keeps the newest bubble pinned to the
          bottom; older bubbles slide up and clip cleanly at the top when the overlay
          fills up. */}
      <div
        className="absolute left-3 right-24 top-16 lg:top-20 bottom-56 lg:bottom-40 z-30 pointer-events-none flex flex-col-reverse gap-1.5 overflow-hidden"
        data-testid="chat-overlay"
      >
        {overlayMsgs.slice(-8).slice().reverse().map((m, i) => (
          <div
            key={m.id || i}
            className="bg-white/15 backdrop-blur-md rounded-2xl px-3.5 py-1.5 text-sm lg:text-xs flex flex-wrap items-baseline gap-x-2 gap-y-0.5 self-start max-w-[85%] shadow-lg"
            style={{ textShadow: "0 1px 2px rgba(0,0,0,0.9), 0 0 4px rgba(0,0,0,0.6)", color: "#ffffff" }}
          >
            <span className="font-display tracking-wider text-[12px] lg:text-[11px] font-semibold shrink-0" style={{ color: "#F4C542" }}>{m.user_name}</span>
            <span className="font-semibold whitespace-pre-wrap break-words" style={{ color: "#ffffff" }}>{m.message}</span>
          </div>
        ))}
      </div>

      {/* Mobile-only floating chat input — always visible, TikTok-style transparent */}
      <form
        onSubmit={(e) => { e.preventDefault(); sendOverlayChat(); }}
        className="lg:hidden absolute left-3 right-20 bottom-3 z-40 flex gap-2 items-center"
        data-testid="chat-overlay-input-wrap"
      >
        <input
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          placeholder={user ? "Say something…" : "Sign in to chat"}
          className="flex-1 bg-black/40 backdrop-blur-md text-sm rounded-full px-4 py-2 border border-white/20 focus:border-nexus-gold/60 focus:outline-none"
          style={{ color: "#ffffff" }}
          data-testid="chat-overlay-input"
          onFocus={() => { if (!user) nav("/auth"); }}
        />
        <button type="submit" className="bg-nexus-gold text-black rounded-full w-10 h-10 flex items-center justify-center font-bold shadow-lg active:scale-95 transition" data-testid="chat-overlay-send">→</button>
      </form>

      {/* RIGHT action rail — TikTok-sleek transparent */}
      <div className="absolute right-3 bottom-32 lg:bottom-24 z-40 flex flex-col gap-5 items-center">
        <button onClick={() => nav(`/u/${s.creator_id}`)} className="relative" data-testid="feed-profile">
          <img src={s.creator_avatar || `https://api.dicebear.com/7.x/identicon/svg?seed=${s.creator_id}`} alt="" className="w-12 h-12 rounded-full ring-2 ring-nexus-gold object-cover" />
          <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-nexus-pink text-black rounded-full w-5 h-5 text-[10px] font-bold flex items-center justify-center">+</span>
        </button>
        <button onClick={tip} className="flex flex-col items-center gap-1" data-testid="feed-like">
          <div className="bg-white/10 backdrop-blur-md w-12 h-12 rounded-full flex items-center justify-center border border-white/10 hover:bg-white/20 transition active:scale-95"><Heart className="w-6 h-6 drop-shadow-lg" style={{ color: "#ffffff", fill: "#ffffff" }} /></div>
          <span className="text-[10px] font-bold drop-shadow" style={{ color: "#ffffff" }}>{Math.floor(Math.random()*99)+1}K</span>
        </button>
        <button onClick={() => setShowStickers((v) => !v)} className="flex flex-col items-center gap-1" data-testid="feed-stickers-btn">
          <div className={`backdrop-blur-md w-12 h-12 rounded-full flex items-center justify-center border border-white/10 transition active:scale-95 ${showStickers ? "bg-nexus-gold/30 border-nexus-gold" : "bg-white/10 hover:bg-white/20"}`}><Smile className="w-6 h-6 drop-shadow-lg" style={{ color: showStickers ? "#F4C542" : "#ffffff" }} /></div>
          <span className="text-[10px] font-bold drop-shadow" style={{ color: "#ffffff" }}>Smash</span>
        </button>
        <button onClick={tip} className="flex flex-col items-center gap-1" data-testid="feed-tip">
          <div className="bg-gradient-to-br from-nexus-gold to-nexus-pink w-12 h-12 rounded-full flex items-center justify-center shadow-lg active:scale-95 transition"><Sparkles className="w-6 h-6 drop-shadow-lg" style={{ color: "#ffffff", fill: "#ffffff" }} /></div>
          <span className="text-[10px] font-bold drop-shadow" style={{ color: "#ffffff" }}>Tip</span>
        </button>
        <button onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/watch/${s.id}`); toast.success("Link copied"); }} className="flex flex-col items-center gap-1" data-testid="feed-share">
          <div className="bg-white/10 backdrop-blur-md w-12 h-12 rounded-full flex items-center justify-center border border-white/10 hover:bg-white/20 transition active:scale-95"><Share2 className="w-6 h-6 drop-shadow-lg" style={{ color: "#ffffff" }} /></div>
          <span className="text-[10px] font-bold drop-shadow" style={{ color: "#ffffff" }}>Share</span>
        </button>
      </div>

      {/* SMASHABLE STICKER PANEL — slides up from bottom, stays open while user rapid-fires */}
      {showStickers && (
        <div className="absolute inset-x-3 bottom-20 lg:bottom-24 z-50 bg-black/85 backdrop-blur-xl rounded-2xl border border-nexus-gold/40 p-3 max-h-[40vh] overflow-y-auto" data-testid="feed-sticker-dropdown">
          <div className="flex items-center justify-between mb-2">
            <div className="font-display text-[10px] tracking-[0.3em] uppercase text-nexus-gold">// smash mode · tap to fire</div>
            <button
              onClick={() => setShowStickers(false)}
              className="bg-white/20 hover:bg-white/30 rounded-full w-8 h-8 flex items-center justify-center transition active:scale-90 border border-white/30"
              data-testid="feed-close-stickers"
              aria-label="Close sticker picker"
              style={{ color: "#ffffff" }}
            >
              <X className="w-5 h-5" strokeWidth={2.5} style={{ color: "#ffffff" }} />
            </button>
          </div>
          <div className="grid grid-cols-5 gap-2">
            {stickers.map((st) => (
              <button
                key={st.id}
                onClick={() => sendSticker(st)}
                title={`${st.name} · ${st.price} NX`}
                data-testid={`feed-sticker-${st.name}`}
                className="aspect-square rounded-xl bg-white/5 hover:bg-white/15 active:scale-90 active:bg-nexus-gold/20 transition flex flex-col items-center justify-center gap-0.5"
              >
                <span className="text-2xl">{st.emoji}</span>
                <span className={`text-[9px] font-bold ${st.price > 0 ? "text-nexus-gold" : "text-white/60"}`}>{st.price > 0 ? `${st.price}NX` : "FREE"}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* BOTTOM info + sponsor banner — keep above mobile input */}
      <div className="absolute left-4 right-20 bottom-14 lg:bottom-6 z-30 space-y-1.5">
        <div className="text-xs text-white/70">@{s.creator_name?.toLowerCase().replace(/\s+/g, "")}</div>
        <div className="font-display font-bold text-base text-white leading-tight">{s.title}</div>
        <div className="text-[11px] text-nexus-gold/80 tracking-wider">{(s.viewers || 0).toLocaleString()} viewing · {idx + 1} / {streams.length}</div>
        <div data-testid="feed-sponsor-banner-wrap"><SponsorBanner surface="stream" creatorId={s.creator_id} /></div>
      </div>
    </div>
  );
}
